// src/components/AuthForms.js

import React from 'react';

// --- Helper Icon Components for a cleaner look ---

const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

// --- Main AuthForms Component ---

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

function AuthForms({
  showLoginForm,
  loginData,
  handleAuthInputChange,
  handleLoginSubmit,
  // 2FA props
  pending2FA,
  totpCode,
  setTotpCode,
  handle2FASubmit,
  onCancel2FA,
}) {
  return (
    // Main container with a sleek background
    <div className="auth-container">
      <div className="auth-panel">
        {/* Branding Panel (Left side) */}
        <div className="auth-branding-panel">
          <div className="p-12 text-white">
            <h1 className="text-4xl font-bold mb-4">LegalOps</h1>
            <p className="text-lg text-gray-200">
              Streamlining legal practice management. Welcome to the future of law firm efficiency.
            </p>
            <div className="mt-8 border-l-4 border-accent pl-4">
              <p className="text-gray-300 italic">"The secret of getting ahead is getting started."</p>
              <p className="text-gray-400 text-sm mt-1">- Mark Twain</p>
            </div>
          </div>
        </div>

        {/* Form Panel (Right side) */}
        <div className="auth-form-panel">
          <div className="max-w-md w-full space-y-8 p-10">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-primary">
                {pending2FA ? 'Two-Factor Authentication' : 'Welcome Back!'}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                {pending2FA ? 'Enter the code from your authenticator app' : 'Sign in to continue'}
              </p>
            </div>

      {/* Notifications are shown via the global Toast component */}

            {/* --- 2FA TOTP code entry --- */}
            {pending2FA && (
              <form className="mt-8 space-y-6" onSubmit={handle2FASubmit}>
                <div className="space-y-4">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                  </div>
                  <div className="input-group">
                    <span className="input-icon"><ShieldIcon /></span>
                    <input
                      id="totpCode"
                      name="totpCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      autoComplete="one-time-code"
                      required
                      autoFocus
                      className="auth-input text-center text-2xl tracking-widest"
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.) and enter the 6-digit code.
                  </p>
                </div>
                <div className="space-y-3">
                  <button type="submit" className="auth-button" disabled={totpCode.length !== 6}>
                    Verify
                  </button>
                  <button
                    type="button"
                    className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                    onClick={onCancel2FA}
                  >
                    Back to login
                  </button>
                </div>
              </form>
            )}

            {!pending2FA && showLoginForm && (
              <form className="mt-8 space-y-6" onSubmit={handleLoginSubmit}>
                <div className="space-y-4">
                  <div className="input-group">
                    <span className="input-icon"><UserIcon /></span>
                    <input
                      id="loginUsername"
                      name="username"
                      type="text"
                      required
                      className="auth-input"
                      placeholder="Username"
                      value={loginData.username}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                  <div className="input-group">
                    <span className="input-icon"><LockIcon /></span>
                    <input
                      id="loginPassword"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      className="auth-input"
                      placeholder="Password"
                      value={loginData.password}
                      onChange={handleAuthInputChange}
                    />
                  </div>
                </div>

                <div>
                  <button type="submit" className="auth-button">
                    Sign In
                  </button>
                </div>
              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthForms;
